import React, { useState } from "react";
import { 
  useCurrencies, 
  addCurrency, 
  updateCurrency, 
  deleteCurrency 
} from "../../controllers/settings.controller";
import { 
  IconCoins, 
  IconPlus, 
  IconEdit, 
  IconTrash, 
  IconCheck, 
  IconCurrencyDollar, 
  IconArrowRight, 
  IconAlertCircle, 
  IconStar,
  IconX
} from "@tabler/icons-react";
import toast from "react-hot-toast";

const PRESET_DENOMINATIONS = {
  NPR: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  USD: [100, 50, 20, 10, 5, 2, 1],
  INR: [500, 200, 100, 50, 20, 10, 5, 2, 1],
  EUR: [500, 200, 100, 50, 20, 10, 5, 2, 1],
  GBP: [50, 20, 10, 5, 2, 1],
  AUD: [100, 50, 20, 10, 5, 2, 1]
};

export default function CurrenciesSettingsPage() {
  const { currencies, isLoading, mutate } = useCurrencies();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [code, setCode] = useState("");
  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [isBase, setIsBase] = useState(false);
  const [denominations, setDenominations] = useState([]);
  const [denomInput, setDenomInput] = useState("");

  const baseCurrency = currencies.find((c) => c.is_base) || currencies[0] || { code: "NPR", symbol: "Rs." };

  const handleOpenAddModal = () => {
    setEditingCurrency(null);
    setCode("");
    setSymbol("");
    setName("");
    setExchangeRate(1);
    setIsBase(false);
    setDenominations([100, 50, 20, 10, 5, 2, 1]);
    setDenomInput("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (curr) => {
    setEditingCurrency(curr);
    setCode(curr.code || "");
    setSymbol(curr.symbol || "");
    setName(curr.name || "");
    setExchangeRate(curr.exchange_rate || 1);
    setIsBase(Boolean(curr.is_base));
    setDenominations(Array.isArray(curr.denominations) ? [...curr.denominations] : []);
    setDenomInput("");
    setIsModalOpen(true);
  };

  const handleAddDenomination = () => {
    const val = parseFloat(denomInput);
    if (!val || val <= 0) {
      toast.error("Please enter a valid positive number for denomination");
      return;
    }
    if (denominations.includes(val)) {
      toast.error("Denomination already added");
      return;
    }
    const updated = [...denominations, val].sort((a, b) => b - a);
    setDenominations(updated);
    setDenomInput("");
  };

  const handleRemoveDenomination = (valToRemove) => {
    setDenominations(denominations.filter((d) => d !== valToRemove));
  };

  const handleApplyPreset = (presetKey) => {
    if (PRESET_DENOMINATIONS[presetKey]) {
      setDenominations([...PRESET_DENOMINATIONS[presetKey]]);
      if (!code) setCode(presetKey);
      if (presetKey === "USD" && !symbol) setSymbol("$");
      if (presetKey === "INR" && !symbol) setSymbol("₹");
      if (presetKey === "EUR" && !symbol) setSymbol("€");
      if (presetKey === "GBP" && !symbol) setSymbol("£");
      toast.success(`Loaded ${presetKey} denominations`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !symbol.trim() || !name.trim()) {
      toast.error("Please fill in code, symbol, and currency name");
      return;
    }

    if (denominations.length === 0) {
      toast.error("Please configure at least one denomination for note/coin counting");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        code: code.trim().toUpperCase(),
        symbol: symbol.trim(),
        name: name.trim(),
        exchange_rate: isBase ? 1.0 : parseFloat(exchangeRate) || 1.0,
        is_base: isBase,
        denominations,
        is_active: 1
      };

      if (editingCurrency) {
        await updateCurrency(editingCurrency.id, payload);
        toast.success("Currency updated successfully!");
      } else {
        await addCurrency(payload);
        toast.success("Currency added successfully!");
      }

      setIsModalOpen(false);
      mutate();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to save currency");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, isCurrBase) => {
    if (isCurrBase) {
      toast.error("Cannot delete the active base currency");
      return;
    }
    if (!confirm("Are you sure you want to delete this currency?")) {
      return;
    }
    try {
      await deleteCurrency(id);
      toast.success("Currency deleted successfully!");
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete currency");
    }
  };

  const handleSetAsBase = async (curr) => {
    if (curr.is_base) return;
    if (!confirm(`Set ${curr.code} (${curr.name}) as the primary store Base Currency? All exchange rates will be evaluated relative to ${curr.code}.`)) {
      return;
    }
    try {
      await updateCurrency(curr.id, {
        ...curr,
        is_base: true,
        exchange_rate: 1.0
      });
      toast.success(`${curr.code} is now the primary Base Currency!`);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to set base currency");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <IconCoins className="text-amber-500" size={28} />
            Currency & Denominations
          </h1>
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
            Configure accepted currencies, foreign exchange rates, and cash denominations for shift drawer reconciliation.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          className="btn btn-sm bg-restro-green hover:bg-restro-green-button-hover text-white border-none gap-2 px-4 shadow-sm"
        >
          <IconPlus size={16} /> Add Currency
        </button>
      </div>

      {/* Base Currency Callout Banner */}
      <div className="bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 flex items-center justify-center font-black text-xl">
            {baseCurrency.symbol || "Rs."}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                Primary Base Currency
              </span>
              <span className="badge badge-sm badge-success text-[10px] font-bold text-white">Active</span>
            </div>
            <div className="text-lg font-black text-gray-900 dark:text-white">
              {baseCurrency.name || "Nepalese Rupee"} ({baseCurrency.code || "NPR"})
            </div>
            <div className="text-[11px] text-gray-500 dark:text-zinc-400">
              Exchange Rate: <span className="font-bold text-gray-800 dark:text-zinc-200">1.0000 (Base)</span>. All financial accounting, reports, and drawer balances reference this currency.
            </div>
          </div>
        </div>

        <div className="text-right sm:text-right w-full sm:w-auto border-t sm:border-t-0 pt-2 sm:pt-0 border-emerald-500/20">
          <span className="text-[11px] text-gray-500 dark:text-zinc-400 block">Total Currencies</span>
          <span className="text-xl font-black text-gray-900 dark:text-white">{currencies.length} configured</span>
        </div>
      </div>

      {/* Currencies List */}
      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <span className="loading loading-spinner loading-md text-emerald-600"></span>
        </div>
      ) : currencies.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-2xl p-12 text-center">
          <IconCoins className="mx-auto text-gray-400 mb-3" size={48} />
          <h3 className="font-bold text-gray-800 dark:text-zinc-200 text-base">No Currencies Configured</h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
            Add currencies to enable multi-currency note & coin counting when cashiers close their register shift.
          </p>
          <button onClick={handleOpenAddModal} className="btn btn-sm btn-outline mt-4">
            Add First Currency
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currencies.map((curr) => {
            const isCurrBase = Boolean(curr.is_base);
            const denoms = Array.isArray(curr.denominations) ? curr.denominations : [];

            return (
              <div 
                key={curr.id}
                className={`bg-white dark:bg-zinc-900 border rounded-2xl p-5 shadow-xs transition hover:shadow-md ${
                  isCurrBase 
                    ? "border-emerald-300 dark:border-emerald-800/80 ring-1 ring-emerald-500/20" 
                    : "border-gray-200 dark:border-zinc-800"
                }`}
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-black text-lg ${
                      isCurrBase 
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" 
                        : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                    }`}>
                      {curr.symbol}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-black text-base text-gray-900 dark:text-white">
                          {curr.name}
                        </span>
                        <span className="badge badge-sm font-bold text-[10px] bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 border-none">
                          {curr.code}
                        </span>
                        {isCurrBase && (
                          <span className="badge badge-sm badge-success text-[10px] font-bold text-white">
                            Base
                          </span>
                        )}
                      </div>

                      {/* Exchange rate */}
                      <div className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5 flex items-center gap-1">
                        {isCurrBase ? (
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">1.0000 (Base Anchor)</span>
                        ) : (
                          <>
                            <span>1 {curr.code}</span>
                            <IconArrowRight size={12} className="text-gray-400" />
                            <span className="font-bold text-gray-800 dark:text-zinc-200">
                              {parseFloat(curr.exchange_rate).toFixed(4)} {baseCurrency.code}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {!isCurrBase && (
                      <button
                        onClick={() => handleSetAsBase(curr)}
                        title="Set as Primary Base Currency"
                        className="btn btn-ghost btn-xs text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                      >
                        <IconStar size={15} />
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEditModal(curr)}
                      title="Edit Currency & Denominations"
                      className="btn btn-ghost btn-xs text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-white"
                    >
                      <IconEdit size={15} />
                    </button>
                    {!isCurrBase && (
                      <button
                        onClick={() => handleDelete(curr.id, isCurrBase)}
                        title="Delete Currency"
                        className="btn btn-ghost btn-xs text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                      >
                        <IconTrash size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Denominations list */}
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-zinc-800">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-zinc-500 mb-2">
                    Shift Cash Denominations ({denoms.length} notes/coins)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {denoms.map((d, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-800 dark:text-zinc-200"
                      >
                        {curr.symbol}{d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Currency Modal */}
      <dialog open={isModalOpen} className={`modal ${isModalOpen ? "modal-open" : ""}`}>
        <div className="modal-box max-w-lg p-0 overflow-hidden rounded-3xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-2xl">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-5 text-white flex justify-between items-center">
            <div>
              <h3 className="font-black text-lg flex items-center gap-2">
                <IconCoins size={22} />
                {editingCurrency ? "Edit Currency & Denominations" : "Add New Currency"}
              </h3>
              <p className="text-xs text-emerald-100 mt-0.5">
                Set code, exchange rate to base, and supported note & coin denominations.
              </p>
            </div>
            <button 
              onClick={() => setIsModalOpen(false)}
              className="btn btn-sm btn-circle btn-ghost text-white"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
            {/* Quick Presets */}
            {!editingCurrency && (
              <div className="bg-gray-50 dark:bg-zinc-800/50 p-3 rounded-xl border border-gray-200 dark:border-zinc-700/50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400 block mb-2">
                  Quick Denomination Presets
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(PRESET_DENOMINATIONS).map((pKey) => (
                    <button
                      key={pKey}
                      type="button"
                      onClick={() => handleApplyPreset(pKey)}
                      className="btn btn-xs btn-outline border-gray-300 dark:border-zinc-600 text-xs hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-950/40"
                    >
                      {pKey}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Currency Code & Symbol */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-1">
                  Currency Code *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. USD, EUR, INR"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="input input-bordered w-full uppercase font-bold text-sm bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-1">
                  Currency Symbol *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. $, Rs., €, ₹"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  className="input input-bordered w-full font-bold text-sm bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700"
                />
              </div>
            </div>

            {/* Currency Name */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-1">
                Currency Name *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. US Dollar, Nepalese Rupee, Indian Rupee"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input input-bordered w-full text-sm bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700"
              />
            </div>

            {/* Exchange Rate to Base Currency */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-1">
                Exchange Rate to Base Currency ({baseCurrency.code || "Base"})
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-zinc-400 shrink-0">1 {code || "CURR"} =</span>
                <input
                  type="number"
                  step="0.0001"
                  required
                  disabled={isBase}
                  value={isBase ? 1 : exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  className="input input-bordered flex-1 font-bold text-sm bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700 disabled:opacity-60"
                  placeholder="1.0000"
                />
                <span className="text-xs font-bold text-gray-700 dark:text-zinc-300 shrink-0">
                  {baseCurrency.symbol || baseCurrency.code}
                </span>
              </div>
              <span className="text-[11px] text-gray-400 mt-1 block">
                {isBase 
                  ? "Base currency exchange rate is locked to 1.0000."
                  : `Amount of ${baseCurrency.code} per 1 unit of ${code || "this currency"}. E.g. 1 USD = 135 NPR.`}
              </span>
            </div>

            {/* Is Base Currency Toggle */}
            <div className="bg-gray-50 dark:bg-zinc-800 p-3 rounded-xl border border-gray-200 dark:border-zinc-700 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-gray-800 dark:text-zinc-200">Set as Primary Base Currency</div>
                <div className="text-[11px] text-gray-500 dark:text-zinc-400">
                  Makes this currency the primary unit across sales and financial ledgers.
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-success toggle-sm"
                checked={isBase}
                onChange={(e) => {
                  setIsBase(e.target.checked);
                  if (e.target.checked) setExchangeRate(1);
                }}
              />
            </div>

            {/* Denominations Builder */}
            <div>
              <label className="block text-xs font-bold text-gray-700 dark:text-zinc-300 uppercase tracking-wider mb-1">
                Accepted Cash Denominations (Notes & Coins) *
              </label>

              {/* Add Denomination Input */}
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  step="any"
                  placeholder="Enter note/coin value, e.g. 500"
                  value={denomInput}
                  onChange={(e) => setDenomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddDenomination();
                    }
                  }}
                  className="input input-bordered input-sm flex-1 text-sm bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700"
                />
                <button
                  type="button"
                  onClick={handleAddDenomination}
                  className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none gap-1"
                >
                  <IconPlus size={16} /> Add
                </button>
              </div>

              {/* Chips container */}
              <div className="bg-gray-50 dark:bg-zinc-800/80 p-3 rounded-xl border border-gray-200 dark:border-zinc-700 min-h-[64px] flex flex-wrap gap-1.5 items-center">
                {denominations.length === 0 ? (
                  <span className="text-xs text-gray-400 italic">No denominations added yet. Add note/coin values above.</span>
                ) : (
                  denominations.map((d, idx) => (
                    <div
                      key={idx}
                      className="badge badge-md bg-white dark:bg-zinc-900 border border-gray-300 dark:border-zinc-700 text-gray-800 dark:text-zinc-200 gap-1.5 pl-2.5 pr-1 font-bold text-xs"
                    >
                      <span>{symbol || ""}{d}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDenomination(d)}
                        className="btn btn-ghost btn-circle btn-xs w-4 h-4 min-h-0 text-gray-400 hover:text-rose-500"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none px-5 gap-1.5"
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <IconCheck size={16} />
                )}
                Save Currency
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </div>
  );
}
